import axios, {AxiosRequestConfig} from 'axios';
import {isAccessTokenExpired, readAuthInfo, refreshAccessTokenCookies} from './PingAuthUtil';
import {PingAuthInfo} from './types/PingAuthInfo';

let injectTokenInterceptor: number | undefined;
let isRefreshingToken: boolean;

const authorizationHeaderName = 'Authorization';

/**
 * For convenience, the below interceptor will auto inject the auth token
 * into any Axios request going to a *.treez.io domain or localhost.
 *
 * This behavior will only trigger if no Authorization header is present,
 * even if the Authorization header value is undefined.
 */
export const injectTokenAxiosRequests = async (enable = true): Promise<void> => {
    // Deregister the existing interceptor, if we have one defined
    if (injectTokenInterceptor !== undefined) {
        axios.interceptors.request.eject(injectTokenInterceptor);
        injectTokenInterceptor = undefined;
    }

    isRefreshingToken = false;

    const authConfigInterceptor = async (
        config: AxiosRequestConfig
    ): Promise<AxiosRequestConfig> => {
        if (!enable) {
            // Disable injecting the auth header
            return config;
        }

        if (!config?.headers || !config?.url) {
            return config;
        }

        const url = new URL(config.url);
        const {hostname} = url;

        if (!hostname.endsWith('.treez.io') && hostname !== 'localhost') {
            // Do not inject if not using the services on our domain
            return config;
        }

        let authInfo: PingAuthInfo | null = readAuthInfo();
        if (authInfo === null) {
            return config;
        }

        // Before we inject the auth token, check its expiry and refresh if needed first
        if (isAccessTokenExpired(authInfo)) {
            // Use a simple flag to prevent a potential injection cycle here
            if (!isRefreshingToken) {
                isRefreshingToken = true;
                authInfo = await refreshAccessTokenCookies();
                isRefreshingToken = false;
            }
        }

        const authHeaderExists = Object.keys(config.headers).find(
            (headerName) => headerName.toUpperCase() === authorizationHeaderName.toUpperCase()
        );

        if (authInfo?.tokens?.accessToken && !authHeaderExists) {
            const authorizationHeaderValue = `Bearer ${authInfo.tokens.accessToken}`;
            return {
                ...config,
                headers: {
                    ...config.headers,
                    [authorizationHeaderName]: authorizationHeaderValue,
                },
            };
        }

        return config;
    };

    // For debug and testing, allow configuring certain URLs to fail
    const withConfiguredFailures = async (
        config: AxiosRequestConfig
    ): Promise<AxiosRequestConfig> => {
        const failurePaths: RegExp[] | undefined = globalThis.debug?.requestFailurePaths;
        if (failurePaths) {
            if (failurePaths.findIndex((regex) => config.url?.match(regex) !== null) !== -1) {
                // Our URL matches one of the regex's configured to fail
                return {
                    ...config,
                    headers: {
                        ...config.headers,
                        [authorizationHeaderName]: 'Bearer TestInvalidToken', // This should induce an 'Invalid Token' error
                    },
                };
            }
        }

        return authConfigInterceptor(config);
    };

    // Delay the start of each request.  We could also put them in a queue and delay between each..
    const withExtraStaticDelay = async (
        config: AxiosRequestConfig
    ): Promise<AxiosRequestConfig> => {
        const msDelay = globalThis.debug?.requestExtraDelay || 50; // Add a default delay for a minimal rate limiting style behaviour

        if (msDelay > 0) {
            await new Promise((resolve) => {
                setTimeout(resolve, msDelay);
            });
        }
        // return authConfigInterceptor(config);
        return withConfiguredFailures(config);
    };

    // injectTokenInterceptor = axios.interceptors.request.use(authConfigInterceptor);
    injectTokenInterceptor = axios.interceptors.request.use(withExtraStaticDelay);
};
